import { useCallback, useContext, useEffect, useMemo } from "react";
import { isEmpty } from "lodash";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { fetchRole } from "@/features/organizations/roles/slice";
import { Access, Role } from "@/types";
import { useParams } from "react-router-dom";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";

const useAccessControl = () => {
  const dispatch = useAppDispatch();
  const { organizationId } = useParams();
  const websiteContext = useContext(WebsiteContext);
  const activeUser = useAppSelector((state) => state?.auth?.user);
  const storeOrganization = useAppSelector((state) => state?.organizations[organizationId ?? ""] ?? null);
  const roles = useAppSelector((store) => store.roles) as unknown as Record<Role, { permissions: string[] }>;

  const activeOrganization = useMemo(() => {
    return websiteContext?.organization || storeOrganization || null;
  }, [storeOrganization, websiteContext?.organization]);

  const fetchRoles = useCallback(async () => {
    if (isEmpty(roles)) {
      await dispatch(fetchRole());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const access: Access | null = useMemo(() => {
    if (!activeUser?.access || !organizationId) return null;
    return activeUser?.access[organizationId] as unknown as Access;
  }, [activeUser?.access, organizationId]);

  const permissions: string[] | null = useMemo(() => {
    if (!access || !roles) return null;
    return roles[access.role]?.permissions;
  }, [access, roles]);

  const isOwner = useMemo(() => {
    const owner = activeOrganization?.createdBy;
    if (!owner || !activeUser?.uid) return false;
    return owner === activeUser?.uid;
  }, [activeOrganization?.createdBy, activeUser?.uid]);

  const hasAccess = useCallback(
    (access: string | string[]) => {
      if (!permissions) return;

      if (typeof access === "string") {
        return permissions.includes(access);
      }

      if (Array.isArray(access)) {
        return access.every((a) => permissions.includes(a));
      }
    },
    [permissions]
  );

  const getRoleName = (role: string) => {
    switch (role) {
      case "owner":
        return "Creador";
      case "admin":
        return "Administrador";
      case "waiter":
        return "Mesero";
      default:
        return role;
    }
  };

  const getActiveUserRole = useCallback(() => {
    if (!activeUser?.access || !activeOrganization?.id) return null;
    const roles = activeUser.access[activeOrganization?.id]?.role ?? null;
    return roles;
  }, [activeUser, activeOrganization]);

  const getRole = useCallback(
    (organizationId: string) => {
      if (!activeUser || !activeUser?.access || !activeOrganization) return null;

      // Check role
      const role = activeUser.access[organizationId]?.role ?? null;
      if (role) return getRoleName(role);

      // Check if owner
      if (activeOrganization.createdBy === activeUser?.uid) return getRoleName("owner");
      return null;
    },
    [activeUser, activeOrganization]
  );

  const isActiveUserAdmin = useMemo(() => {
    const role = getActiveUserRole();
    if (!role) return false;
    return ["admin", "super-admin"].includes(role);
  }, [getActiveUserRole]);

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isOwner,
    getRole,
    hasAccess,
    fetchRoles,
    getActiveUserRole,
    isActiveUserAdmin,
  };
};

export default useAccessControl;
